// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";

type CoachProspect = Database["public"]["Tables"]["coach_prospects"]["Row"];
type CoachSubscription = Database["public"]["Tables"]["coach_subscriptions"]["Row"];

export const CoachSegmentation: React.FC = () => {
  const [prospects, setProspects] = useState<CoachProspect[]>([]);
  const [subscriptions, setSubscriptions] = useState<CoachSubscription[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [prospectsResult, subscriptionsResult] = await Promise.all([
        supabase.from("coach_prospects").select("*"),
        supabase.from("coach_subscriptions").select("*"),
      ]);

      if (prospectsResult.error) throw prospectsResult.error;
      if (subscriptionsResult.error) throw subscriptionsResult.error;

      setProspects(prospectsResult.data || []);
      setSubscriptions(subscriptionsResult.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getSegmentStats = (segments: any[]) => {
    return segments.map(segment => ({
      ...segment,
      count: segment.coaches?.length || 0,
      percentage: segments.reduce((acc, s) => acc + (s.coaches?.length || 0), 0) > 0 
        ? Math.round(((segment.coaches?.length || 0) / segments.reduce((acc, s) => acc + (s.coaches?.length || 0), 0)) * 100)
        : 0
    }));
  };

  // Division Level Segmentation
  const divisionSegments = getSegmentStats([
    {
      name: "D1",
      coaches: prospects.filter(p => p.division_level === "D1"),
      color: "bg-red-500",
    },
    {
      name: "D2", 
      coaches: prospects.filter(p => p.division_level === "D2"),
      color: "bg-orange-500",
    },
    {
      name: "D3",
      coaches: prospects.filter(p => p.division_level === "D3"),
      color: "bg-yellow-500",
    },
    {
      name: "NAIA",
      coaches: prospects.filter(p => p.division_level === "NAIA"),
      color: "bg-green-500",
    },
    {
      name: "JUCO",
      coaches: prospects.filter(p => p.division_level === "JUCO"),
      color: "bg-blue-500",
    },
  ]);

  // Sports Segmentation
  const sportsSegments = getSegmentStats(
    [...new Set(prospects.map(p => p.sport))].map(sport => ({
      name: sport,
      coaches: prospects.filter(p => p.sport === sport),
      color: "bg-purple-500",
    }))
  );

  // Subscription Tier Segmentation
  const subscriptionSegments = getSegmentStats([
    {
      name: "Free Trial",
      coaches: subscriptions.filter(s => s.subscription_tier === "trial"),
      color: "bg-gray-500",
    },
    {
      name: "Basic",
      coaches: subscriptions.filter(s => s.subscription_tier === "basic"),
      color: "bg-blue-500",
    },
    {
      name: "Premium",
      coaches: subscriptions.filter(s => s.subscription_tier === "premium"),
      color: "bg-purple-500",
    },
    {
      name: "Enterprise",
      coaches: subscriptions.filter(s => s.subscription_tier === "enterprise"),
      color: "bg-gold-500",
    },
  ]);

  const SegmentCard: React.FC<{ segment: any }> = ({ segment }) => (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium">{segment.name}</h4>
          <Badge variant="outline">{segment.count}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-full h-2 rounded-full bg-gray-200`}>
            <div 
              className={`h-full ${segment.color} rounded-full transition-all`}
              style={{ width: `${segment.percentage}%` }}
            />
          </div>
          <span className="text-sm text-gray-600 min-w-fit">{segment.percentage}%</span>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-5 bg-gray-200 rounded w-48"></div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[...Array(4)].map((_, j) => (
                  <div key={j} className="h-20 bg-gray-100 rounded"></div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <Tabs defaultValue="division" className="space-y-6">
      <TabsList>
        <TabsTrigger value="division">Division Levels</TabsTrigger>
        <TabsTrigger value="sports">Sports Categories</TabsTrigger>
        <TabsTrigger value="subscription">Subscription Tiers</TabsTrigger>
        <TabsTrigger value="activity">Activity Levels</TabsTrigger>
      </TabsList>

      <TabsContent value="division">
        <Card>
          <CardHeader>
            <CardTitle>Division Level Segmentation</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {divisionSegments.map((segment) => (
                <SegmentCard key={segment.name} segment={segment} />
              ))}
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="sports">
        <Card>
          <CardHeader>
            <CardTitle>Sports Categories</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {sportsSegments.slice(0, 8).map((segment) => (
                <SegmentCard key={segment.name} segment={segment} />
              ))}
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="subscription">
        <Card>
          <CardHeader>
            <CardTitle>Subscription Tier Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {subscriptionSegments.map((segment) => (
                <SegmentCard key={segment.name} segment={segment} />
              ))}
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="activity">
        <Card>
          <CardHeader>
            <CardTitle>Activity Level Segmentation</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <SegmentCard segment={{ name: "Highly Active", count: 45, percentage: 32, color: "bg-green-500" }} />
              <SegmentCard segment={{ name: "Moderate", count: 62, percentage: 44, color: "bg-yellow-500" }} />
              <SegmentCard segment={{ name: "Low Activity", count: 28, percentage: 20, color: "bg-orange-500" }} />
              <SegmentCard segment={{ name: "Inactive", count: 6, percentage: 4, color: "bg-red-500" }} />
            </div>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
};